import { useState } from "react";
import { BarChart, Bar, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";

const axis = { tick: { fontFamily: "JetBrains Mono", fontSize: 10, fill: "#525252" }, axisLine: { stroke: "#E5E5E5" }, tickLine: false };
const boxStyle = { background: "#050505", border: "1px solid #262626", fontFamily: "JetBrains Mono", fontSize: 12, color: "#fff", padding: "8px 10px" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (h) => String(h).padStart(2, "0");

// "2026-10-06" -> day-month: "06-10" (axis) / "06 Oct" (tooltip)
const dm = (iso) => `${iso.slice(8, 10)}-${iso.slice(5, 7)}`;
const dmLong = (iso) => `${iso.slice(8, 10)} ${MONTHS[Number(iso.slice(5, 7)) - 1]}`;

const VIEWS = [["avg", "All days avg"], ["today", "Today"], ["24h", "Last 24 h"]];

function DailyTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={boxStyle} data-testid="daily-tooltip">
      <div style={{ color: "#A3A3A3" }}>{dmLong(d.date)}</div>
      <div>Avg load <b>{d.avg_w != null ? `${d.avg_w} W` : "—"}</b></div>
      <div style={{ color: "#A3A3A3" }}>Energy {d.kwh} kWh</div>
    </div>
  );
}

function HourTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const h = payload[0].payload;
  const label = h.day ? `${h.day} ${pad(h.hour)}:00` : `${pad(h.hour)}:00`;
  return (
    <div style={boxStyle} data-testid="hourly-tooltip">
      <div style={{ color: "#A3A3A3" }}>{label}</div>
      {h.n === 0 ? <div>No data</div> : (
        <>
          <div>Avg <b>{h.kw} kW</b></div>
          <div style={{ color: "#A3A3A3" }}>Min {h.min_kw} · Max {h.max_kw} kW</div>
          <div style={{ color: "#A3A3A3" }}>{h.n} reading{h.n === 1 ? "" : "s"}</div>
        </>
      )}
    </div>
  );
}

export default function UsageCharts({ usage }) {
  const [view, setView] = useState("avg");
  const daily = usage.daily.slice(-30).map((d) => ({ ...d, label: dm(d.date) }));
  const avg = usage.avg_daily_kwh;

  const series = { avg: usage.hourly, today: usage.hourly_today, "24h": usage.hourly_24h }[view] || usage.hourly;
  // Hours with no readings are gaps (null), never a misleading 0 kW.
  // (`n` is missing only if an older backend answers — then fall back to kw > 0.)
  const data = series.map((h, i) => {
    const has = h.n === undefined ? h.kw > 0 : h.n > 0;
    return { ...h, i, n: has ? (h.n ?? 1) : 0, avgKw: has ? h.kw : null, band: has && h.min_kw != null ? [h.min_kw, h.max_kw] : null };
  });
  const withData = data.filter((h) => h.n > 0);
  const peakHour = withData.length ? withData.reduce((m, h) => (h.kw > m.kw ? h : m)) : null;
  const xTick = (i) => { const h = data[i]; return h ? pad(h.hour) : ""; };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-px bg-kl-lineLight border border-kl-lineLight" data-testid="usage-charts">
      <div className="lg:col-span-3 bg-white p-5">
        <div className="flex items-baseline justify-between mb-4">
          <div><p className="kl-label">Daily consumption</p><h3 className="font-display text-lg mt-0.5">Last {daily.length} day{daily.length === 1 ? "" : "s"}</h3></div>
          <span className="font-mono text-xs text-kl-mutedLight">avg {avg} kWh</span>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={daily} barCategoryGap={3}>
            <XAxis dataKey="label" {...axis} interval="preserveStartEnd" />
            <YAxis {...axis} width={34} />
            <Tooltip content={<DailyTip />} cursor={{ fill: "#F5F5F5" }} />
            <ReferenceLine y={avg} stroke="#00C853" strokeDasharray="3 3" />
            <Bar dataKey="kwh" isAnimationActive={false} maxBarSize={28}>
              {daily.map((d, i) => <Cell key={i} fill={d.kwh >= avg * 1.15 ? "#00E676" : "#050505"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="lg:col-span-2 bg-kl-black text-white p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div><p className="kl-label-dark">Hourly load profile</p><h3 className="font-display text-lg mt-0.5">Avg kW by hour</h3></div>
          <span className="font-mono text-xs text-kl-green">{peakHour ? `peak ${peakHour.day ? `${peakHour.day} ` : ""}${pad(peakHour.hour)}:00` : "no data"}</span>
        </div>
        <div className="flex gap-px mb-3 border border-kl-line w-fit" data-testid="hourly-view-toggle">
          {VIEWS.map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`hourly-view-${k}`}
              className={`px-2.5 h-7 text-[11px] font-mono ${view === k ? "bg-white text-kl-black" : "text-kl-muted"}`}>{l}</button>
          ))}
        </div>
        {withData.length === 0 && <p className="text-xs font-mono text-kl-muted py-8 text-center" data-testid="hourly-empty">No readings in this period yet.</p>}
        <ResponsiveContainer width="100%" height={withData.length ? 196 : 1}>
          <ComposedChart data={data}>
            <XAxis dataKey="i" tick={{ ...axis.tick, fill: "#A3A3A3" }} axisLine={{ stroke: "#262626" }} tickLine={false} interval={2} tickFormatter={xTick} />
            <YAxis tick={{ ...axis.tick, fill: "#A3A3A3" }} axisLine={false} tickLine={false} width={34} />
            <Tooltip content={<HourTip />} cursor={{ stroke: "#262626" }} />
            {peakHour && <ReferenceLine x={peakHour.i} stroke="#00E676" strokeDasharray="2 4" />}
            <Area type="stepAfter" dataKey="band" stroke="none" fill="#00E676" fillOpacity={0.18} isAnimationActive={false} connectNulls={false} />
            <Line type="stepAfter" dataKey="avgKw" stroke="#FFFFFF" strokeWidth={2} dot={{ r: 2, fill: "#fff", strokeWidth: 0 }} isAnimationActive={false} connectNulls={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <p className="mt-1 text-[10px] font-mono text-kl-muted">Line = average · shaded = quietest to busiest reading · gaps = no data</p>
      </div>
    </div>
  );
}
